import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";
import { BsArrowLeftCircleFill, BsArrowRightCircleFill } from "react-icons/bs";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showFirstLast?: boolean;
  siblingCount?: number;
  totalItems?: number;
  currentLimit?: number;
  onLimitChange?: (limit: number) => void;
  limitOptions?: number[];
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true,
  siblingCount = 1,
  totalItems = 0,
  currentLimit = 20,
  onLimitChange,
  limitOptions = [20, 30, 50, 100],
}) => {
  const { t } = useTranslation();

  const getPages = () => {
    const pages: (number | string)[] = [];
    const leftSibling = currentPage - siblingCount;
    const rightSibling = currentPage + siblingCount;
    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < totalPages - 1;

    pages.push(1);

    if (showLeftDots) {
      pages.push("...");
    }

    const start = Math.max(2, currentPage - siblingCount);
    const end = Math.min(totalPages - 1, currentPage + siblingCount);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (showRightDots) {
      pages.push("...");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const pages = getPages();

  const baseButtonClass =
    "flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40";

  const normalButtonClass = `${baseButtonClass} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white`;

  const activeButtonClass = `${baseButtonClass} bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, type: "spring" }}
      className="my-6 flex flex-col items-center justify-center gap-4 sm:flex-row"
    >
      <div className="flex items-center justify-center gap-2">
        {showFirstLast && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className={`h-9 w-9 ${normalButtonClass}`}
          >
            <BsArrowRightCircleFill className="h-4 w-4 ltr:rotate-180" />
          </motion.button>
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`h-9 gap-1 px-3 ${normalButtonClass}`}
        >
          <HiOutlineChevronRight className="h-4 w-4 ltr:rotate-180" />
          <span className="hidden sm:inline">{t("buttons.previous")}</span>
        </motion.button>

        <div className="flex items-center gap-1.5">
          <AnimatePresence mode="wait">
            {pages.map((page, index) => (
              <motion.div
                key={typeof page === "number" ? page : `dots-${index}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ delay: index * 0.05 }}
              >
                {page === "..." ? (
                  <span className="flex h-9 w-9 items-center justify-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    ...
                  </span>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onPageChange(page as number)}
                    className={`h-9 w-9 ${
                      currentPage === page
                        ? activeButtonClass
                        : normalButtonClass
                    }`}
                  >
                    {page}
                  </motion.button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`h-9 gap-1 px-3 ${normalButtonClass}`}
        >
          <span className="hidden sm:inline">{t("buttons.next")}</span>
          <HiOutlineChevronLeft className="h-4 w-4 ltr:rotate-180" />
        </motion.button>

        {showFirstLast && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className={`h-9 w-9 ${normalButtonClass}`}
          >
            <BsArrowLeftCircleFill className="h-4 w-4 ltr:rotate-180" />
          </motion.button>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
        {totalItems > 0 && (
          <span>
            {t("buttons.total")} : {totalItems}
          </span>
        )}

        {onLimitChange && (
          <div className="flex items-center gap-2">
            <label htmlFor="limit-select" className="text-sm">
              {t("buttons.itemsPerPage")}
            </label>
            <select
              id="limit-select"
              value={currentLimit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            >
              {limitOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Pagination;
